/**
 * La destinée d'une note de crédit : sera-t-elle rendue en argent, ou pas ?
 *
 * ## Les trois vies d'une note, et pourquoi les confondre coûte cher
 *
 * 1. **Elle se déduit.** La famille n'avait pas payé — ou pas tout. La note
 *    réduit ce que la facture réclame, la famille verse le solde, et **plus
 *    rien n'est dû dans aucun sens**. Cas réel et majoritaire : une facture
 *    pour deux stages, un stage annulé avant paiement, une note pour la moitié,
 *    et le parent ne paie que le reste. Cette note **ne sera jamais remboursée**.
 *
 * 2. **Elle vaut de l'argent, et la famille le garde.** La facture était payée.
 *    L'argent reçu n'est plus réclamé : il reste au crédit de la famille et
 *    viendra réduire une prochaine facture. C'est le comportement par défaut —
 *    elle n'a rien à faire.
 *
 * 3. **Elle est rendue.** La famille a demandé son argent. Le virement part, sa
 *    communication porte le numéro de la note, et le rapprochement l'éteint.
 *
 * **Seules les vies 2 et 3 sont de l'argent.** La vie 1 est une réduction de
 * créance, et l'afficher comme une dette fabrique une dette qui n'existe pas.
 *
 * ## Ce que la confusion coûtait, mesuré le 13 septembre 2026
 *
 * | Destinée | Notes | Montant |
 * |---|---|---|
 * | `deduite` — ne sera jamais rendue | **926** | **56 941,25 €** |
 * | `a_rendre` | 44 | 1 730,00 € |
 * | `rendue` | 36 | 1 866,20 € |
 * | `partiellement_rendue` | 1 | 92,00 € |
 *
 * L'écran annonçait **58 672,25 €** « encore remboursables » : le total des
 * notes non rendues, déductions comprises. Soit **34 fois** ce qui est
 * réellement dû.
 *
 * ## L'arithmétique, et le piège qu'elle contourne
 *
 * Pour une facture : `A` brut, `C` la somme de ses notes, `P` l'argent encaissé.
 *
 *     net                = A − C
 *     trop-perçu E       = max(0, P − max(0, net))
 *     adossé aux notes   = least(E, C)          ← de l'argent à rendre
 *     C − least(E, C)                           ← n'a JAMAIS été de l'argent
 *
 * `P` est le **maximum** du nouveau circuit et de l'ancien, jamais leur somme :
 * 447 factures sont payées selon `invoices.total_payments` et inconnues de
 * `payment_allocations`, et les additionner compterait deux fois le même argent.
 *
 * La répartition de `adossé` entre les notes d'une même facture est **FIFO**,
 * par date d'émission puis par numéro. Le prorata serait plus « juste » en
 * moyenne, mais il produit des centimes non entiers et surtout **aucune note ne
 * serait jamais soldée** : chaque nouvelle note rouvrirait les précédentes.
 *
 * ## Un plancher, et non une vérité définitive
 *
 * `cashAdosseCents` ne compte que l'argent qu'on peut **prouver** aujourd'hui.
 * Le rapprochement des paiements entrants n'est pas terminé — 5 528 paiements
 * n'ont encore aucune allocation — donc une note `deduite` aujourd'hui peut
 * devenir `a_rendre` demain, sans que personne ne touche à la note.
 *
 * C'est la raison pour laquelle la destinée **ne filtre rien** : elle informe.
 * Un écran qui cacherait les notes `deduite` cacherait aussi celles dont le
 * paiement n'est pas encore encodé — et 18 des 36 notes réellement remboursées
 * portent sur une facture que la comptabilité croit non réglée.
 */

export type Destinee =
  /** Elle réduit une facture non payée. Rien ne sera rendu. */
  | 'deduite'
  /** De l'argent a été reçu au-delà du net : il est dû à la famille. */
  | 'a_rendre'
  /** Une partie a été rendue. */
  | 'partiellement_rendue'
  /** Tout a été rendu. */
  | 'rendue';

export interface NoteAQualifier {
  amountCents: number;
  refundedCents: number;
  /** Le trop-perçu de sa facture qui lui revient, réparti FIFO. */
  cashAdosseCents: number;
}

/** La destinée d'une note, dans l'ordre où les cas se décident. */
export function destineeDeLaNote(n: NoteAQualifier): Destinee {
  if (n.refundedCents >= n.amountCents && n.amountCents > 0) return 'rendue';
  if (n.refundedCents > 0) return 'partiellement_rendue';
  return n.cashAdosseCents > 0 ? 'a_rendre' : 'deduite';
}

/** Ce qu'il reste à rendre en argent : jamais plus que l'argent prouvé. */
export function aRendreCents(n: NoteAQualifier): number {
  return Math.max(0, Math.min(n.cashAdosseCents, n.amountCents - n.refundedCents));
}

export interface LibelleDeDestinee {
  label: string;
  /** Ce que cela veut dire, en une phrase, pour l'écran. */
  explication: string;
  ton: 'positif' | 'attention' | 'neutre';
}

export const LIBELLES_DE_DESTINEE: Record<Destinee, LibelleDeDestinee> = {
  deduite: {
    label: 'Déduite',
    explication:
      "Elle réduit une facture que la famille n'avait pas payée. La famille verse le solde, "
      + "et il n'y a rien à lui rendre.",
    ton: 'neutre',
  },
  a_rendre: {
    label: 'À rendre',
    explication:
      "De l'argent a été reçu au-delà de ce que la facture réclamait. Il appartient à la "
      + 'famille : elle peut le garder en provision ou en demander le remboursement.',
    ton: 'attention',
  },
  partiellement_rendue: {
    label: 'Partiellement rendue',
    explication: 'Une partie du montant a déjà été virée à la famille.',
    ton: 'attention',
  },
  rendue: {
    label: 'Rendue',
    explication: 'Le montant a été viré à la famille et le virement est rapproché.',
    ton: 'positif',
  },
};

/**
 * L'avertissement à montrer quand on s'apprête à rembourser une note `deduite`.
 *
 * Rend `null` quand il n'y a rien à dire. **Ce n'est jamais un refus** : le
 * rapprochement des paiements n'étant pas terminé, l'argent peut très bien
 * avoir été versé sans être encore encodé. C'est l'humain qui sait, pas la vue.
 */
export function avertissementAvantRemboursement(n: NoteAQualifier): string | null {
  if (destineeDeLaNote(n) !== 'deduite') return null;
  return "Aucun encaissement connu ne dépasse le net de cette facture : cette note semble "
    + "avoir simplement réduit ce que la famille devait, auquel cas il n'y a rien à lui "
    + "rendre. Si le paiement existe mais n'est pas encore encodé, le placement reste juste.";
}
